import type { ChooseOutcome } from "./auth.ts";

/**
 * A choice of character refused straight after the page's own leave (ADR-084's amendment of 2026-10-13, the finding
 * of that date). The page waits out its character's stay on its own clock, the answer's ticks and a margin, and then
 * shows character select; the map server counts the same stay in ticks, and a server that ran late ends it a little
 * after the page's wait. A player who then chooses another character at once is refused with the gateway's
 * `lingering` for a character that is leaving that very moment. So the page asks once more after a short wait, and
 * shows the refusal only when the second answer refuses too. A refusal that does not follow the page's own leave is
 * a character truly still in the world, played from another tab or left in a fight, and shows at once.
 */

/**
 * The gateway's code for a choice refused because another character of the account is still in the world.
 */
const LINGERING = "lingering";

/**
 * How many times a refused choice is asked again.
 */
export const OWN_LEAVE_RETRIES = 1;

/**
 * How long the page waits before it asks again, in milliseconds: three times the margin the stay's dialog adds, and
 * short enough that the busy button reads as one press.
 */
export const OWN_LEAVE_RETRY_WAIT_MS = 1_500;

/**
 * How long after the page's own leave a refusal still counts as following it, in milliseconds. A choice made later
 * has given a late server that long already, so its refusal is a character that is not leaving.
 */
export const OWN_LEAVE_WINDOW_MS = 5_000;

/**
 * What the page does with a refused choice.
 */
export type RefusalStep = { readonly kind: "show" } | { readonly kind: "retry"; readonly waitMs: number };

let endedAt: number | null = null;

/**
 * Notes that the page's own leave has ended: the stay of the character it left by Change character or Log out has
 * run out on the page's clock.
 *
 * @param now The page's clock, in milliseconds.
 */
export function noteOwnLeave(now: number): void {
  endedAt = now;
}

/**
 * When the page's own leave ended.
 *
 * @returns The time noted, or null when the page has left nothing.
 */
export function ownLeaveAt(): number | null {
  return endedAt;
}

/**
 * Forgets the page's own leave, once a character is in the world again.
 */
export function forgetOwnLeave(): void {
  endedAt = null;
}

/**
 * Decides what follows a refused choice of character.
 *
 * @param code The gateway's code for the refusal.
 * @param tries How many times the choice has been asked again already.
 * @param leftAt When the page's own leave ended, or null for none.
 * @param now The page's clock as the refusal arrives, in milliseconds.
 * @returns `retry` with the wait for the still-in-the-world refusal on the first try within the window after the
 *   page's own leave; `show` for every other.
 */
export function afterRefusal(code: string, tries: number, leftAt: number | null, now: number): RefusalStep {
  const since = leftAt === null ? -1 : now - leftAt;
  if (code !== LINGERING || tries >= OWN_LEAVE_RETRIES || since < 0 || since > OWN_LEAVE_WINDOW_MS) {
    return { kind: "show" };
  }
  return { kind: "retry", waitMs: OWN_LEAVE_RETRY_WAIT_MS };
}

/**
 * Chooses a character, asking once more when the choice is refused straight after the page's own leave
 * (`afterRefusal`).
 *
 * @param choose Sends the choice and settles with its outcome; never rejects.
 * @param page When the page's own leave ended, the page's clock, and its wait.
 * @returns The outcome to act on: the first, or the second when the first was tried again.
 */
export async function chooseAfterOwnLeave(choose: () => Promise<ChooseOutcome>, page: { readonly leftAt: number | null; readonly now: () => number; readonly wait: (ms: number) => Promise<void> }): Promise<ChooseOutcome> {
  let outcome = await choose();
  for (let tries = 0; !outcome.ok; tries += 1) {
    const step = afterRefusal(outcome.code, tries, page.leftAt, page.now());
    if (step.kind === "show") {
      break;
    }
    await page.wait(step.waitMs);
    outcome = await choose();
  }
  return outcome;
}
